import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Table, TableBody, TableCell, TableHead, 
  TableHeader, TableRow 
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Edit, Trash2, Upload, Video, FileText,
  GripVertical, Eye, Play, Check, X, Clock,
  Monitor, Smartphone, Tablet, TrendingUp, BarChart3, Search, Sparkles,
  Share2, Save, MousePointerClick, Gift, Award
} from 'lucide-react';
import { RichTextEditor } from '../components/RichTextEditor';
import { AIAssistant } from '../components/AIAssistant';
import { CertificatePreview } from '../components/CertificatePreview';

import { getApiBaseUrl } from '@/lib/apiBase';
import { buildVideoSrc } from '@/lib/videoSrc';
const API_URL = getApiBaseUrl();

// Admin Lessons Management
export const AdminLessons = () => {
  const { token } = useAuth();
  const { t, tFormat } = useTranslation();
  const [lessons, setLessons] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [aiAssistantOpen, setAiAssistantOpen] = useState(false);
  const [formData, setFormData] = useState({
    course_id: '', title: '', description: '', 
    videos: [{ type: 'youtube', url: '', title: 'Part 1' }], // Multiple videos support
    notes_url: '', duration_minutes: 15, is_free: false, order: 0
  });

  const headers = { Authorization: `Bearer ${token}` };

  const handleAIInsert = (content, type) => {
    if (type === 'text') {
      setFormData(prev => ({ ...prev, description: content }));
    }
    setAiAssistantOpen(false);
  };

  const fetchData = async () => {
    try {
      const [lessonsRes, coursesRes] = await Promise.all([
        axios.get(`${API_URL}/admin/lessons${selectedCourse ? `?course_id=${selectedCourse}` : ''}`, { headers }),
        axios.get(`${API_URL}/admin/courses`, { headers })
      ]);
      setLessons(Array.isArray(lessonsRes.data) ? lessonsRes.data : []);
      // Handle both array and paginated response for courses
      const coursesData = coursesRes.data?.courses || coursesRes.data || [];
      setCourses(Array.isArray(coursesData) ? coursesData : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token, selectedCourse]);

  const handleVideoUpload = async (e, videoIndex) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    
    try {
      const res = await axios.post(`${API_URL}/admin/upload/video`, uploadData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      const newVideos = [...(formData?.videos || [])];
      newVideos[videoIndex] = { ...(newVideos[videoIndex] || {}), url: res?.data?.url || res?.data?.video_url || '', type: 'local' };
      setFormData({ ...formData, videos: newVideos });
      toast.success(t('admin_toast_video_upload_ok', 'Video uploaded successfully'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_toast_video_upload_fail', 'Failed to upload video'));
    } finally {
      setUploading(false);
    }
  };

  const addVideo = () => {
    const currentVideos = formData?.videos || [];
    const newVideos = [...currentVideos, { type: 'youtube', url: '', title: `Part ${currentVideos.length + 1}` }];
    setFormData({ ...formData, videos: newVideos });
  };

  const removeVideo = (index) => {
    const currentVideos = formData?.videos || [];
    if (currentVideos.length <= 1) {
      toast.error(t('admin_lesson_min_one_video', 'Lesson must have at least one video'));
      return;
    }
    const newVideos = currentVideos.filter((_, i) => i !== index);
    // Update part numbers
    const updatedVideos = newVideos.map((v, i) => ({ ...v, title: `Part ${i + 1}` }));
    setFormData({ ...formData, videos: updatedVideos });
  };

  const updateVideo = (index, field, value) => {
    const newVideos = [...formData.videos];
    newVideos[index] = { ...newVideos[index], [field]: value };
    setFormData({ ...formData, videos: newVideos });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate course is selected
    if (!formData?.course_id) {
      toast.error(t('admin_lesson_select_course', 'Please select a course'));
      return;
    }
    
    try {
      if (editingLesson) {
        await axios.put(`${API_URL}/admin/lessons/${editingLesson?.id}`, formData, { headers });
        toast.success(t('admin_toast_lesson_updated', 'Lesson updated'));
      } else {
        await axios.post(`${API_URL}/admin/lessons`, formData, { headers });
        toast.success(t('admin_toast_lesson_created', 'Lesson created'));
      }
      setDialogOpen(false);
      setEditingLesson(null);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_toast_lesson_save_fail', 'Failed to save lesson'));
    }
  };

  const handleEdit = (lesson) => {
    if (!lesson) return;
    setEditingLesson(lesson);
    // Convert old single video format to new multi-video format if needed
    const videos = lesson?.videos || (lesson?.video_url || lesson?.youtube_url ? [{
      type: lesson?.video_type || 'youtube',
      url: lesson?.video_url || lesson?.youtube_url || '',
      title: 'Part 1'
    }] : [{ type: 'youtube', url: '', title: 'Part 1' }]);
    setFormData({ ...lesson, videos });
    setDialogOpen(true);
  };

  const handleDelete = async (lessonId) => {
    if (!lessonId || !window.confirm(t('admin_confirm_delete_lesson', 'Delete this lesson?'))) return;
    try {
      await axios.delete(`${API_URL}/admin/lessons/${lessonId}`, { headers });
      toast.success(t('admin_toast_lesson_deleted', 'Lesson deleted'));
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_toast_lesson_delete_fail', 'Failed to delete lesson'));
    }
  };

  const resetForm = () => {
    setFormData({
      course_id: selectedCourse || '', title: '', description: '', 
      videos: [{ type: 'youtube', url: '', title: 'Part 1' }],
      notes_url: '', duration_minutes: 15, is_free: false, order: 0
    });
  };

  // Filter lessons by search query
  const filteredLessons = lessons.filter(lesson => 
    lesson.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lesson.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div data-testid="admin-lessons">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-serif font-medium">Lessons</h1>
          <p className="text-muted-foreground">{lessons.length} total lessons</p>
        </div>
        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input 
              placeholder="Search lessons..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-64"
            />
          </div>
          {/* Course Filter */}
          <Select value={selectedCourse || 'all'} onValueChange={(v) => setSelectedCourse(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Courses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Courses</SelectItem>
              {courses.map((course) => (
                <SelectItem key={course.id} value={course.id}>{course.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setAiAssistantOpen(true)} data-testid="ai-assistant-btn">
            <Sparkles className="w-4 h-4 mr-2" />
            AI Assistant
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditingLesson(null); resetForm(); }} data-testid="create-lesson-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Lesson
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingLesson ? 'Edit Lesson' : 'Create Lesson'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Course *</Label>
                  <Select value={formData.course_id || 'select'} onValueChange={(v) => setFormData({...formData, course_id: v === 'select' ? '' : v})}>
                    <SelectTrigger><SelectValue placeholder="Select course" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="select" disabled>Select a course</SelectItem>
                      {courses.map((course) => (
                        <SelectItem key={course.id} value={course.id}>{course.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Title *</Label>
                  <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required />
                </div>
                <div>
                  <Label>Description *</Label>
                  <RichTextEditor 
                    value={formData.description} 
                    onChange={(value) => setFormData({...formData, description: value})}
                    placeholder="Write lesson description with rich formatting..."
                  />
                </div>
                
                {/* Multiple Videos Section */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-medium">Videos ({formData.videos?.length || 0})</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addVideo}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add Video
                    </Button>
                  </div>
                  
                  {formData.videos?.map((video, index) => (
                    <Card key={index} className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-medium text-sm">{video.title || `Part ${index + 1}`}</span>
                        {formData.videos.length > 1 && (
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeVideo(index)}>
                            <X className="w-4 h-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                      
                      <div className="space-y-3">
                        <div>
                          <Label>Video Type</Label>
                          <Select value={video.type} onValueChange={(v) => updateVideo(index, 'type', v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="youtube">YouTube Embed</SelectItem>
                              <SelectItem value="local">Upload Video</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {video.type === 'youtube' ? (
                          <div>
                            <Label>YouTube URL</Label>
                            <Input 
                              value={video.url} 
                              onChange={(e) => updateVideo(index, 'url', e.target.value)} 
                              placeholder="https://www.youtube.com/watch?v=..." 
                            />
                            {video.url && (
                              <div className="mt-2 aspect-video bg-black rounded-lg overflow-hidden max-w-2xl">
                                <iframe 
                                  src={video.url.replace('watch?v=', 'embed/')}
                                  className="w-full h-full"
                                  allowFullScreen
                                />
                              </div>
                            )}
                          </div>
                        ) : (
                          <div>
                            <Label>Upload Video</Label>
                            <div className="flex items-center gap-4">
                              <Input type="file" accept="video/*" onChange={(e) => handleVideoUpload(e, index)} disabled={uploading} />
                              {uploading && <span className="text-sm text-muted-foreground">Uploading...</span>}
                            </div>
                            {video.url && (
                              <div className="mt-2">
                                <p className="text-sm text-green-600">Video uploaded</p>
                                <div className="mt-2 aspect-video bg-black rounded-lg overflow-hidden max-w-2xl">
                                  <video src={buildVideoSrc(video.url, token)} controls className="w-full h-full object-contain" />
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Duration (minutes)</Label>
                    <Input type="number" value={formData.duration_minutes} onChange={(e) => setFormData({...formData, duration_minutes: parseInt(e.target.value)})} />
                  </div>
                  <div>
                    <Label>Order</Label>
                    <Input type="number" value={formData.order} onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})} />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={formData.is_free} onCheckedChange={(v) => setFormData({...formData, is_free: v})} />
                  <Label>Free Lesson</Label>
                </div>
                <DialogFooter>
                  <Button type="submit">{editingLesson ? 'Update' : 'Create'}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Course</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLessons.map((lesson) => (
                <TableRow key={lesson.id}>
                  <TableCell>{lesson.order}</TableCell>
                  <TableCell className="font-medium">{lesson.title}</TableCell>
                  <TableCell>{lesson.course_title || '-'}</TableCell>
                  <TableCell>{lesson.duration_minutes} min</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">{lesson.video_type}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={lesson.is_free ? 'secondary' : 'default'}>
                      {lesson.is_free ? 'Free' : 'Premium'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(lesson)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(lesson.id)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <AIAssistant 
        isOpen={aiAssistantOpen} 
        onClose={() => setAiAssistantOpen(false)} 
        onInsert={handleAIInsert}
        token={token}
      />
    </div>
  );
};

// Admin Quizzes Management
export const AdminQuizzes = () => {
  const { token } = useAuth();
  const { t, tFormat } = useTranslation();
  const [quizzes, setQuizzes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [formData, setFormData] = useState({
    lesson_id: '', title: '', questions: [], passing_score: 70, is_graded: true,
    timer_enabled: false, time_per_question: 60, anti_cheat_enabled: false
  });
  const [currentQuestion, setCurrentQuestion] = useState({
    question: '', options: ['', '', '', ''], correct_answer: 0, explanation: ''
  });
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
  const [resultsQuiz, setResultsQuiz] = useState(null);
  const [quizResults, setQuizResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const [quizzesRes, lessonsRes, coursesRes] = await Promise.all([
        axios.get(`${API_URL}/admin/quizzes`, { headers }),
        axios.get(`${API_URL}/admin/lessons`, { headers }),
        axios.get(`${API_URL}/admin/courses`, { headers })
      ]);
      setQuizzes(Array.isArray(quizzesRes.data) ? quizzesRes.data : []);
      setLessons(Array.isArray(lessonsRes.data) ? lessonsRes.data : []);
      const coursesData = coursesRes.data?.courses || coursesRes.data || [];
      setCourses(Array.isArray(coursesData) ? coursesData : []);
    } catch (err) {
      console.error(err);
      setQuizzes([]);
      setLessons([]);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  // Filter quizzes by search and course
  const filteredQuizzes = quizzes.filter(quiz => {
    const matchesSearch = quiz.title?.toLowerCase().includes(searchQuery.toLowerCase());
    const lesson = lessons.find(l => l.id === quiz.lesson_id);
    const matchesCourse = !selectedCourse || lesson?.course_id === selectedCourse;
    return matchesSearch && matchesCourse;
  });

  // Filter lessons by selected course for the form
  const filteredLessons = selectedCourse 
    ? lessons.filter(l => l.course_id === selectedCourse)
    : lessons;

  const addQuestion = () => {
    if (!currentQuestion.question || currentQuestion.options.some(o => !o)) {
      toast.error(t('admin_toast_quiz_fill_fields', 'Please fill all question fields'));
      return;
    }
    setFormData({
      ...formData,
      questions: [...formData.questions, { ...currentQuestion }]
    });
    setCurrentQuestion({ question: '', options: ['', '', '', ''], correct_answer: 0, explanation: '' });
  };

  const removeQuestion = (index) => {
    setFormData({
      ...formData,
      questions: formData.questions.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate lesson is selected
    if (!formData?.lesson_id) {
      toast.error(t('admin_toast_quiz_select_lesson', 'Please select a lesson'));
      return;
    }
    
    if (!formData?.questions?.length) {
      toast.error(t('admin_toast_quiz_need_one', 'Add at least one question'));
      return;
    }
    try {
      if (editingQuiz) {
        await axios.put(`${API_URL}/admin/quizzes/${editingQuiz?.id}`, formData, { headers });
        toast.success(t('admin_toast_quiz_updated', 'Quiz updated'));
      } else {
        await axios.post(`${API_URL}/admin/quizzes`, formData, { headers });
        toast.success(t('admin_toast_quiz_created', 'Quiz created'));
      }
      setDialogOpen(false);
      setEditingQuiz(null);
      setFormData({
        lesson_id: '', title: '', questions: [], passing_score: 70, is_graded: true,
        timer_enabled: false, time_per_question: 60, anti_cheat_enabled: false
      });
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_toast_quiz_save_fail', 'Failed to save quiz'));
    }
  };

  const handleEdit = (quiz) => {
    if (!quiz) return;
    setEditingQuiz(quiz);
    setFormData({
      lesson_id: quiz?.lesson_id || '',
      title: quiz?.title || '',
      questions: quiz?.questions || [],
      passing_score: quiz?.passing_score || 70,
      is_graded: quiz?.is_graded !== false,
      timer_enabled: quiz?.timer_enabled || false,
      time_per_question: quiz?.time_per_question || 60,
      anti_cheat_enabled: quiz?.anti_cheat_enabled || false
    });
    setDialogOpen(true);
  };

  const openResults = async (quiz) => {
    setResultsQuiz(quiz);
    setResultsDialogOpen(true);
    setResultsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/quizzes/${quiz.id}/results`, { headers });
      setQuizResults(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load quiz results');
      setQuizResults([]);
    } finally {
      setResultsLoading(false);
    }
  };

  const handleDelete = async (quizId) => {
    if (!quizId || !window.confirm(t('admin_confirm_delete_quiz', 'Delete this quiz?'))) return;
    try {
      await axios.delete(`${API_URL}/admin/quizzes/${quizId}`, { headers });
      toast.success(t('admin_toast_quiz_deleted', 'Quiz deleted'));
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_toast_quiz_delete_fail', 'Failed to delete quiz'));
    }
  };

  return (
    <div data-testid="admin-quizzes">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-serif font-medium">Quizzes</h1>
          <p className="text-muted-foreground">{quizzes.length} total quizzes</p>
        </div>
        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input 
              placeholder="Search quizzes..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-64"
            />
          </div>
          {/* Course Filter */}
          <Select value={selectedCourse || 'all'} onValueChange={(v) => setSelectedCourse(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Courses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Courses</SelectItem>
              {courses.map((course) => (
                <SelectItem key={course.id} value={course.id}>{course.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditingQuiz(null); setFormData({ lesson_id: '', title: '', questions: [], passing_score: 70, is_graded: true, timer_enabled: false, time_per_question: 60, anti_cheat_enabled: false }); }}>
                <Plus className="w-4 h-4 mr-2" />
                Create Quiz
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingQuiz ? 'Edit Quiz' : 'Create Quiz'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Lesson</Label>
                  <Select value={formData.lesson_id || 'select'} onValueChange={(v) => setFormData({...formData, lesson_id: v === 'select' ? '' : v})}>
                    <SelectTrigger><SelectValue placeholder="Select lesson" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="select" disabled>Select a lesson</SelectItem>
                      {lessons.map((lesson) => (
                        <SelectItem key={lesson.id} value={lesson.id}>{lesson.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Passing Score (%)</Label>
                  <Input type="number" value={formData.passing_score} onChange={(e) => setFormData({...formData, passing_score: parseInt(e.target.value)})} disabled={!formData.is_graded} />
                </div>
              </div>

              {/* Graded toggle */}
              <div className="flex items-start justify-between gap-4 rounded-lg border bg-muted/30 p-4" data-testid="quiz-graded-toggle-row">
                <div className="space-y-1">
                  <Label htmlFor="is_graded_switch" className="text-base font-medium">Graded Quiz</Label>
                  <p className="text-xs text-muted-foreground">
                    {formData.is_graded
                      ? 'Students see a percentage score and a pass/fail result. Score counts toward points.'
                      : 'Practice mode — students get instant feedback per question, but no score or pass/fail.'}
                  </p>
                </div>
                <Switch
                  id="is_graded_switch"
                  checked={!!formData.is_graded}
                  onCheckedChange={(v) => setFormData({...formData, is_graded: v})}
                  data-testid="quiz-is-graded-switch"
                />
              </div>

              {/* Timer toggle */}
              <div className="flex items-start justify-between gap-4 rounded-lg border bg-muted/30 p-4" data-testid="quiz-timer-toggle-row">
                <div className="space-y-1 flex-1">
                  <Label htmlFor="timer_enabled_switch" className="text-base font-medium">Enable Timer</Label>
                  <p className="text-xs text-muted-foreground">
                    Shows a per-question countdown. When time runs out the question is auto-submitted as wrong.
                  </p>
                  {formData.timer_enabled && (
                    <div className="mt-2 max-w-xs">
                      <Label className="text-xs">Time per question (seconds)</Label>
                      <Input
                        type="number"
                        min={10}
                        max={300}
                        value={formData.time_per_question}
                        onChange={(e) => setFormData({...formData, time_per_question: parseInt(e.target.value) || 60})}
                        data-testid="quiz-time-per-question-input"
                      />
                    </div>
                  )}
                </div>
                <Switch
                  id="timer_enabled_switch"
                  checked={!!formData.timer_enabled}
                  onCheckedChange={(v) => setFormData({...formData, timer_enabled: v})}
                  data-testid="quiz-timer-enabled-switch"
                />
              </div>

              {/* Anti-cheat toggle */}
              <div className="flex items-start justify-between gap-4 rounded-lg border bg-muted/30 p-4" data-testid="quiz-anti-cheat-toggle-row">
                <div className="space-y-1">
                  <Label htmlFor="anti_cheat_switch" className="text-base font-medium">Anti-cheat Mode</Label>
                  <p className="text-xs text-muted-foreground">
                    Detects tab switches/window blur, blocks copy/paste and devtools shortcuts, and shows a watermark during the attempt.
                  </p>
                </div>
                <Switch
                  id="anti_cheat_switch"
                  checked={!!formData.anti_cheat_enabled}
                  onCheckedChange={(v) => setFormData({...formData, anti_cheat_enabled: v})}
                  data-testid="quiz-anti-cheat-switch"
                />
              </div>

              <div>
                <Label>Quiz Title</Label>
                <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required />
              </div>

              {/* Questions List */}
              <div>
                <Label>Questions ({formData.questions.length})</Label>
                <div className="space-y-2 mt-2">
                  {formData.questions.map((q, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <span className="font-medium">{i + 1}. {q.question}</span>
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeQuestion(i)}>
                        <X className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Add Question */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Add Question</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Question</Label>
                    <Input value={currentQuestion.question} onChange={(e) => setCurrentQuestion({...currentQuestion, question: e.target.value})} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {currentQuestion.options.map((opt, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="correct"
                          checked={currentQuestion.correct_answer === i}
                          onChange={() => setCurrentQuestion({...currentQuestion, correct_answer: i})}
                        />
                        <Input
                          placeholder={`Option ${i + 1}`}
                          value={opt}
                          onChange={(e) => {
                            const newOpts = [...currentQuestion.options];
                            newOpts[i] = e.target.value;
                            setCurrentQuestion({...currentQuestion, options: newOpts});
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  <div>
                    <Label>Explanation (shown after answer)</Label>
                    <Textarea value={currentQuestion.explanation} onChange={(e) => setCurrentQuestion({...currentQuestion, explanation: e.target.value})} />
                  </div>
                  <Button type="button" variant="outline" onClick={addQuestion}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Question
                  </Button>
                </CardContent>
              </Card>

              <DialogFooter>
                <Button type="submit">{editingQuiz ? 'Update Quiz' : 'Create Quiz'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>

      <Card>
        <CardContent className="p-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Lesson</TableHead>
                <TableHead>Questions</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead>Passing Score</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredQuizzes.map((quiz) => (
                <TableRow key={quiz.id}>
                  <TableCell className="font-medium">{quiz.title}</TableCell>
                  <TableCell>{quiz.lesson_title || '-'}</TableCell>
                  <TableCell>{quiz.questions?.length || 0}</TableCell>
                  <TableCell>
                    {quiz.is_graded === false ? (
                      <Badge variant="secondary">Practice</Badge>
                    ) : (
                      <Badge>Graded</Badge>
                    )}
                  </TableCell>
                  <TableCell>{quiz.is_graded === false ? '—' : `${quiz.passing_score}%`}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="icon" onClick={() => openResults(quiz)} title="View Results">
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(quiz)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(quiz.id)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Quiz Results Dialog */}
      <Dialog open={resultsDialogOpen} onOpenChange={setResultsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Results — {resultsQuiz?.title}</DialogTitle>
          </DialogHeader>
          {resultsLoading ? (
            <p className="text-muted-foreground text-sm">Loading...</p>
          ) : quizResults.length === 0 ? (
            <p className="text-muted-foreground text-sm">No submissions yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Student</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead>Violations</TableHead>
                  <TableHead>Submitted</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quizResults.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <div className="font-medium">{r.user_name}</div>
                      <div className="text-xs text-muted-foreground">{r.user_email}</div>
                    </TableCell>
                    <TableCell>{r.score}%</TableCell>
                    <TableCell>
                      {r.passed ? <Badge>Passed</Badge> : <Badge variant="secondary">Failed</Badge>}
                    </TableCell>
                    <TableCell>
                      {r.violations_count > 0 ? (
                        <Badge variant="destructive">{r.violations_count}</Badge>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {r.created_at ? new Date(r.created_at).toLocaleString() : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Admin Video Analytics
export const AdminVideoAnalytics = () => {
  const { token } = useAuth();
  const { t, tFormat } = useTranslation();
  const [dashboard, setDashboard] = useState(null);
  const [lessonAnalytics, setLessonAnalytics] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchDashboard();
  }, [token, days]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/analytics/video/dashboard?days=${days}`, { headers });
      setDashboard(res?.data || null);
    } catch (err) {
      console.error(err);
      // Fallback for older API
      try {
        const res = await axios.get(`${API_URL}/admin/analytics/videos`, { headers });
        setLessonAnalytics(Array.isArray(res?.data) ? res.data : []);
      } catch (e) {
        setLessonAnalytics([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchLessonDetails = async (lessonId) => {
    if (!lessonId) return;
    try {
      const res = await axios.get(`${API_URL}/admin/analytics/video/lesson/${lessonId}?days=${days}`, { headers });
      setSelectedLesson(res?.data || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_toast_video_analytics_fail', 'Failed to load lesson analytics'));
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds) || seconds === 0) return '0s';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${(seconds / 3600).toFixed(1)}h`;
  };

  const getDeviceIcon = (device) => {
    switch (device) {
      case 'mobile': return <Smartphone className="w-4 h-4" />;
      case 'tablet': return <Tablet className="w-4 h-4" />;
      default: return <Monitor className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-video-analytics">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-serif font-medium">Video Analytics</h1>
          <p className="text-muted-foreground">Track viewer engagement and performance</p>
        </div>
        <Select value={days.toString()} onValueChange={(v) => setDays(parseInt(v))}>
          <SelectTrigger className="w-[150px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {dashboard ? (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Eye className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{dashboard.total_views.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">Total Views</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{dashboard.total_watch_time_hours}h</p>
                    <p className="text-sm text-muted-foreground">Watch Time</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{dashboard.completion_rate}%</p>
                    <p className="text-sm text-muted-foreground">Completion Rate</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{formatDuration(dashboard.avg_watch_duration_seconds)}</p>
                    <p className="text-sm text-muted-foreground">Avg. Duration</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Top Lessons */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Top Performing Lessons</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Lesson</TableHead>
                      <TableHead className="text-right">Views</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dashboard.top_lessons?.map((lesson, i) => (
                      <TableRow key={lesson._id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-medium">
                              {i + 1}
                            </span>
                            <span className="font-medium">{lesson.title}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{lesson.views.toLocaleString()}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => fetchLessonDetails(lesson._id)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!dashboard.top_lessons || dashboard.top_lessons.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                          No video views recorded yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Device Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Device Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {dashboard.device_breakdown?.map((device) => (
                  <div key={device._id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getDeviceIcon(device._id)}
                      <span className="capitalize">{device._id || 'Unknown'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Progress 
                        value={(device.count / dashboard.total_views) * 100} 
                        className="w-20 h-2" 
                      />
                      <span className="text-sm text-muted-foreground w-12 text-right">
                        {device.count}
                      </span>
                    </div>
                  </div>
                ))}
                {(!dashboard.device_breakdown || dashboard.device_breakdown.length === 0) && (
                  <p className="text-center text-muted-foreground py-4">No data available</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Views by Day Chart (Simple) */}
          {dashboard.views_by_day && dashboard.views_by_day.length > 0 && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-lg">Views Over Time</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-1 h-40">
                  {dashboard.views_by_day.map((day, i) => {
                    const maxViews = Math.max(...dashboard.views_by_day.map(d => d.views));
                    const height = maxViews > 0 ? (day.views / maxViews) * 100 : 0;
                    return (
                      <div 
                        key={i} 
                        className="flex-1 bg-accent/20 hover:bg-accent/40 transition-colors rounded-t"
                        style={{ height: `${Math.max(height, 2)}%` }}
                        title={`${day._id}: ${day.views} views`}
                      />
                    );
                  })}
                </div>
                <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                  <span>{dashboard.views_by_day[0]?._id}</span>
                  <span>{dashboard.views_by_day[dashboard.views_by_day.length - 1]?._id}</span>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        /* Fallback to old table view */
        <Card>
          <CardContent className="p-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lesson</TableHead>
                  <TableHead>Views</TableHead>
                  <TableHead>Completions</TableHead>
                  <TableHead>Completion Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lessonAnalytics.map((item) => (
                  <TableRow key={item.lesson_id}>
                    <TableCell className="font-medium">{item.lesson_title}</TableCell>
                    <TableCell>{item.views}</TableCell>
                    <TableCell>{item.completions}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={item.completion_rate} className="w-24 h-2" />
                        <span className="text-sm">{Math.round(item.completion_rate)}%</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {lessonAnalytics.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                      No video analytics data yet
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Lesson Details Dialog */}
      <Dialog open={!!selectedLesson} onOpenChange={() => setSelectedLesson(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedLesson?.lesson?.title}</DialogTitle>
          </DialogHeader>
          {selectedLesson && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-muted rounded-lg text-center">
                  <p className="text-2xl font-bold">{selectedLesson.unique_viewers}</p>
                  <p className="text-sm text-muted-foreground">Unique Viewers</p>
                </div>
                <div className="p-4 bg-muted rounded-lg text-center">
                  <p className="text-2xl font-bold">{selectedLesson.completions}</p>
                  <p className="text-sm text-muted-foreground">Completions</p>
                </div>
              </div>
              <div className="p-4 bg-accent/10 rounded-lg text-center">
                <p className="text-3xl font-bold text-accent">{selectedLesson.completion_rate}%</p>
                <p className="text-sm text-muted-foreground">Completion Rate</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Admin Projects Management
export const AdminProjects = () => {
  const { token } = useAuth();
  const { t, tFormat } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/projects?status=${filter}`, { headers });
      setProjects(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token, filter]);

  const updateStatus = async (projectId, status) => {
    if (!projectId) return;
    try {
      await axios.put(`${API_URL}/admin/projects/${projectId}/status?status=${status}`, {}, { headers });
      toast.success(tFormat('admin_toast_project_status_fmt', { status }, 'Project {status}'));
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_toast_project_status_fail', 'Failed to update status'));
    }
  };

  return (
    <div data-testid="admin-projects">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-serif font-medium">Student Projects</h1>
          <p className="text-muted-foreground">Review and approve student submissions</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project) => (
          <Card key={project.id}>
            {project.images?.length > 0 && (
              <img 
                src={`${process.env.REACT_APP_BACKEND_URL}${project.images[0]}`}
                alt={project.title}
                className="w-full aspect-video object-cover rounded-t-lg"
              />
            )}
            <CardContent className="p-4">
              <Badge variant={
                project.status === 'approved' ? 'default' :
                project.status === 'rejected' ? 'destructive' : 'secondary'
              } className="mb-2 capitalize">
                {project.status}
              </Badge>
              <h3 className="font-semibold mb-1">{project.title}</h3>
              <p className="text-sm text-muted-foreground mb-2">by {project.user_name}</p>
              <p className="text-sm line-clamp-2 mb-4">{project.description}</p>
              
              {project.status === 'pending' && (
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => updateStatus(project.id, 'approved')}>
                    <Check className="w-4 h-4 mr-1" />
                    Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => updateStatus(project.id, 'rejected')}>
                    <X className="w-4 h-4 mr-1" />
                    Reject
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        {projects.length === 0 && (
          <Card className="col-span-full">
            <CardContent className="p-12 text-center text-muted-foreground">
              No {filter} projects
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// Admin Affiliates Management
export const AdminAffiliates = () => {
  const { token } = useAuth();
  const { t, tFormat } = useTranslation();
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/affiliates`, { headers });
      setAffiliates(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setAffiliates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const processPayout = async (userId, amount) => {
    if (!userId) return;
    try {
      await axios.post(`${API_URL}/admin/affiliates/${userId}/payout?amount=${amount}`, {}, { headers });
      toast.success(t('admin_toast_payout_ok', 'Payout processed'));
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_toast_payout_fail', 'Failed to process payout'));
    }
  };

  return (
    <div data-testid="admin-affiliates">
      <div className="mb-8">
        <h1 className="text-3xl font-serif font-medium">Affiliates</h1>
        <p className="text-muted-foreground">Referral program management</p>
      </div>

      <Card>
        <CardContent className="p-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Referrals</TableHead>
                <TableHead>Converted</TableHead>
                <TableHead>Earnings</TableHead>
                <TableHead>Pending</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {affiliates.map((aff) => (
                <TableRow key={aff.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{aff.user_name}</p>
                      <p className="text-sm text-muted-foreground">{aff.user_email}</p>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono">{aff.code}</TableCell>
                  <TableCell>{aff.total_referrals}</TableCell>
                  <TableCell>{aff.successful_referrals}</TableCell>
                  <TableCell>₹{aff.total_earnings?.toLocaleString() || 0}</TableCell>
                  <TableCell>₹{aff.pending_earnings?.toLocaleString() || 0}</TableCell>
                  <TableCell>
                    {aff.pending_earnings > 0 && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => processPayout(aff.user_id, aff.pending_earnings)}
                      >
                        Pay Out
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {affiliates.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    No affiliates yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};


// ==================== REVENUE ANALYTICS ====================
export const AdminRevenueAnalytics = () => {
  const { token } = useAuth();
  const { t, tFormat } = useTranslation();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [transactions, setTransactions] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [revenueRes, transactionsRes] = await Promise.all([
          axios.get(`${API_URL}/admin/analytics/revenue?period=${period}`, { headers }),
          axios.get(`${API_URL}/admin/transactions`, { headers })
        ]);
        setAnalytics(revenueRes?.data || null);
        setTransactions(Array.isArray(transactionsRes?.data) ? transactionsRes.data : []);
      } catch (err) {
        console.error(err);
        setAnalytics(null);
        setTransactions([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, period]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Calculate MRR (Monthly Recurring Revenue)
  const calculateMRR = () => {
    const monthlyPayments = (transactions || []).filter(t => 
      t?.payment_status === 'paid' && t?.plan_type === 'monthly'
    );
    return monthlyPayments.reduce((sum, t) => sum + (t?.final_amount || 0), 0);
  };

  // Calculate plan distribution
  const getPlanDistribution = () => {
    const distribution = { monthly: 0, quarterly: 0, yearly: 0 };
    transactions.filter(t => t.payment_status === 'paid').forEach(t => {
      if (distribution[t.plan_type] !== undefined) {
        distribution[t.plan_type]++;
      }
    });
    return distribution;
  };

  // Get recent transactions
  const recentTransactions = transactions.slice(0, 10);

  // Calculate max revenue for chart scaling
  const maxDailyRevenue = analytics?.daily_revenue?.length > 0 
    ? Math.max(...analytics.daily_revenue.map(d => d.revenue || 0))
    : 0;

  const planDist = getPlanDistribution();
  const totalPaidTransactions = planDist.monthly + planDist.quarterly + planDist.yearly;

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-muted rounded w-48"></div>
        <div className="grid grid-cols-4 gap-6">
          {[1,2,3,4].map(i => <div key={i} className="h-32 bg-muted rounded"></div>)}
        </div>
        <div className="h-64 bg-muted rounded"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-revenue">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-serif font-medium">Revenue Analytics</h1>
          <p className="text-muted-foreground">Track your platform's financial performance</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">7 Days</SelectItem>
            <SelectItem value="30d">30 Days</SelectItem>
            <SelectItem value="90d">90 Days</SelectItem>
            <SelectItem value="365d">1 Year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Revenue</p>
                <p className="text-3xl font-bold text-green-600">{formatCurrency(analytics?.total_revenue)}</p>
                <p className="text-xs text-muted-foreground mt-1">Last {period.replace('d', ' days')}</p>
              </div>
              <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Transactions</p>
                <p className="text-3xl font-bold">{analytics?.total_transactions || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">Successful payments</p>
              </div>
              <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg. Transaction</p>
                <p className="text-3xl font-bold">
                  {formatCurrency(analytics?.total_transactions > 0 
                    ? analytics.total_revenue / analytics.total_transactions 
                    : 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Per payment</p>
              </div>
              <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">MRR Estimate</p>
                <p className="text-3xl font-bold text-amber-600">{formatCurrency(calculateMRR())}</p>
                <p className="text-xs text-muted-foreground mt-1">Monthly recurring</p>
              </div>
              <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Revenue Over Time Chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Revenue Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            {analytics?.daily_revenue?.length > 0 ? (
              <div className="h-64 flex items-end gap-1">
                {analytics.daily_revenue.map((day, idx) => {
                  const height = maxDailyRevenue > 0 
                    ? (day.revenue / maxDailyRevenue) * 100 
                    : 0;
                  return (
                    <div 
                      key={idx}
                      className="flex-1 group relative"
                    >
                      <div 
                        className="bg-accent hover:bg-accent/80 rounded-t transition-all cursor-pointer"
                        style={{ height: `${Math.max(height, 2)}%` }}
                      />
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                        <div className="bg-popover text-popover-foreground text-xs rounded px-2 py-1 shadow-lg whitespace-nowrap">
                          <p className="font-medium">{formatCurrency(day.revenue)}</p>
                          <p className="text-muted-foreground">{day._id}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted-foreground">
                No revenue data for this period
              </div>
            )}
          </CardContent>
        </Card>

        {/* Plan Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Plan Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(planDist).map(([plan, count]) => {
                const percentage = totalPaidTransactions > 0 
                  ? (count / totalPaidTransactions) * 100 
                  : 0;
                const colors = {
                  monthly: 'bg-blue-500',
                  quarterly: 'bg-purple-500',
                  yearly: 'bg-green-500'
                };
                return (
                  <div key={plan}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium capitalize">{plan}</span>
                      <span className="text-sm text-muted-foreground">{count} ({percentage.toFixed(0)}%)</span>
                    </div>
                    <Progress value={percentage} className={`h-2 [&>div]:${colors[plan]}`} />
                  </div>
                );
              })}
            </div>

            {/* Revenue by Plan */}
            <div className="mt-6 pt-6 border-t">
              <h4 className="text-sm font-medium mb-3">Revenue by Plan</h4>
              <div className="space-y-2">
                {analytics?.revenue_by_plan?.map((item) => (
                  <div key={item._id} className="flex items-center justify-between text-sm">
                    <span className="capitalize">{item._id || 'Unknown'}</span>
                    <span className="font-medium">{formatCurrency(item.revenue)}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Transactions */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Coupon</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentTransactions.map((tx) => (
                <TableRow key={tx.id}>
                  <TableCell>{new Date(tx.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="font-medium">{tx.user_id?.slice(0, 8)}...</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">{tx.plan_type}</Badge>
                  </TableCell>
                  <TableCell className="capitalize">{tx.payment_method}</TableCell>
                  <TableCell>
                    {tx.coupon_code ? (
                      <Badge variant="secondary">{tx.coupon_code}</Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div>
                      <span className="font-medium">{formatCurrency(tx.final_amount)}</span>
                      {tx.discount_applied > 0 && (
                        <span className="text-xs text-green-600 ml-1">
                          (-{formatCurrency(tx.discount_applied)})
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={tx.payment_status === 'paid' ? 'default' : 'secondary'}>
                      {tx.payment_status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
              {recentTransactions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    No transactions yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};


// ==================== CERTIFICATE TEMPLATE DESIGNER ====================
export const AdminCertificateDesigner = () => {
  const { token } = useAuth();
  const { t, tFormat } = useTranslation();
  const DEFAULT_TEMPLATE = {
    template_id: 'warm_craft',
    layout: 'centered_classic',
    background_color: '#FAF9F6',
    border_color: '#A67C52',
    text_color: '#2A2A2A',
    accent_color: '#A67C52',
    title_text: 'Certificate of Completion',
    subtitle_text: 'This is to certify that',
    completion_text: 'has successfully completed the course',
    organization_name: 'THACHARI',
    organization_tagline: 'Master Interior Design & Carpentry',
    logo_url: '',
    show_border: true,
    show_seal: true,
    seal_image_url: '',
    show_photo: true,
    photo_position: 'top-right',
    signatories: [],
    font_family: 'serif',
    title_size: 48,
    name_size: 36,
    course_size: 28,
    ornament: 'none',
    presentation_text: 'THIS CERTIFICATE IS PROUDLY PRESENTED TO',
    date_label: 'DATE OF ISSUE',
    left_panel_text: ''
  };
  // Fields a preset controls; the rest (text content, images, signatories) stay untouched when applying a preset,
  // unless the preset itself defines them (e.g. "Thachari Curve" ships its own text/signatory defaults)
  const PRESET_FIELDS = [
    'layout', 'background_color', 'border_color', 'text_color', 'accent_color',
    'font_family', 'title_size', 'name_size', 'course_size', 'show_border', 'ornament',
    'title_text', 'subtitle_text', 'completion_text', 'organization_name', 'organization_tagline',
    'presentation_text', 'date_label', 'left_panel_text', 'logo_url',
    'show_seal', 'show_photo', 'photo_position', 'signatories'
  ];
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingField, setUploadingField] = useState(null);
  const [previewName, setPreviewName] = useState('John Doe');
  const [previewCourse, setPreviewCourse] = useState('Introduction to Interior Design');

  const headers = { Authorization: `Bearer ${token}` };
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const handleTemplateImageUpload = async (e, fieldKey) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const uploadData = new FormData();
    uploadData.append('file', file);
    setUploadingField(fieldKey);
    try {
      const res = await axios.post(`${API_URL}/admin/certificate-template/upload`, uploadData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      const url = res?.data?.url || '';
      if (fieldKey === 'seal_image_url') {
        setTemplate(prev => ({ ...prev, seal_image_url: url }));
      } else if (fieldKey.startsWith('signatory_')) {
        const idx = parseInt(fieldKey.split('_')[1], 10);
        setTemplate(prev => {
          const signatories = [...(prev.signatories || [])];
          signatories[idx] = { ...(signatories[idx] || {}), image_url: url };
          return { ...prev, signatories };
        });
      }
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingField(null);
      e.target.value = '';
    }
  };

  const addSignatory = () => {
    setTemplate(prev => ({
      ...prev,
      signatories: [...(prev.signatories || []), { image_url: '', name: '', title: '', show: true }]
    }));
  };

  const updateSignatory = (idx, field, value) => {
    setTemplate(prev => {
      const signatories = [...(prev.signatories || [])];
      signatories[idx] = { ...(signatories[idx] || {}), [field]: value };
      return { ...prev, signatories };
    });
  };

  const removeSignatory = (idx) => {
    setTemplate(prev => ({
      ...prev,
      signatories: (prev.signatories || []).filter((_, i) => i !== idx)
    }));
  };

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const res = await axios.get(`${API_URL}/admin/certificate-template`, { headers });
        if (res?.data) {
          setTemplate(prev => ({ ...prev, ...res.data }));
        }
      } catch (err) {
        console.log('Using default template');
      } finally {
        setLoading(false);
      }
    };
    const fetchPresets = async () => {
      try {
        const res = await axios.get(`${API_URL}/admin/certificate-templates/presets`, { headers });
        setPresets(Array.isArray(res?.data) ? res.data : []);
      } catch (err) {
        console.log('Could not load template presets');
      }
    };
    fetchTemplate();
    fetchPresets();
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/certificate-template`, template, { headers });
      toast.success(t('admin_toast_cert_saved', 'Certificate template saved successfully'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_toast_cert_save_fail', 'Failed to save template'));
    } finally {
      setSaving(false);
    }
  };

  // Selecting a card applies the preset's layout/palette/typography to the form +
  // live preview instantly (unsaved until "Save Template"); content/images/signatories are kept
  const applyPreset = (preset) => {
    setTemplate(prev => {
      const next = { ...prev, template_id: preset.id };
      PRESET_FIELDS.forEach(field => {
        if (field in preset) next[field] = preset[field];
      });
      return next;
    });
  };

  const handleReset = () => {
    const preset = presets.find(p => p.id === template.template_id);
    setTemplate(preset ? { ...DEFAULT_TEMPLATE, ...preset, template_id: preset.id } : DEFAULT_TEMPLATE);
  };

  const selectedPreset = presets.find(p => p.id === template.template_id);
  const maxSignatories = selectedPreset?.max_signatories || 3;
  const isThacharyCurve = template.layout === 'thachary_curve';

  if (loading) {
    return <div className="animate-pulse">Loading template...</div>;
  }

  return (
    <div data-testid="admin-certificate-designer">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-serif font-medium">Certificate Designer</h1>
          <p className="text-muted-foreground">Customize the look of completion certificates</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleReset}>Reset to Template Defaults</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Template'}
          </Button>
        </div>
      </div>

      {/* Template Gallery */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Choose a Template</CardTitle>
          <p className="text-sm text-muted-foreground">
            Pick a starting point, then customize colors, text and typography below.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {presets.map((preset) => {
              const isSelected = template.template_id === preset.id;
              const previewTemplate = { ...DEFAULT_TEMPLATE, ...preset, template_id: preset.id, signatories: [] };
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => applyPreset(preset)}
                  className={`text-left rounded-lg border-2 p-2 transition-colors ${isSelected ? 'border-primary ring-2 ring-primary/30' : 'border-transparent hover:border-muted-foreground/30'}`}
                >
                  <CertificatePreview template={previewTemplate} previewName="John Doe" previewCourse="Sample Course" backendUrl={BACKEND_URL} />
                  <div className="mt-2 flex items-center justify-between gap-1">
                    <p className="text-xs font-medium truncate">{preset.name}</p>
                    {isSelected && <Badge variant="default" className="text-[10px] px-1.5 py-0">Selected</Badge>}
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        {/* Settings Panel */}
        <div className="space-y-6">
          {/* Colors */}
          <Card>
            <CardHeader>
              <CardTitle>Colors</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{isThacharyCurve ? 'Header / Left Panel' : 'Background'}</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="color"
                      value={template.background_color}
                      onChange={(e) => setTemplate({...template, background_color: e.target.value})}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={template.background_color}
                      onChange={(e) => setTemplate({...template, background_color: e.target.value})}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>{isThacharyCurve ? 'Content Area Background' : 'Border'}</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="color"
                      value={template.border_color}
                      onChange={(e) => setTemplate({...template, border_color: e.target.value})}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={template.border_color}
                      onChange={(e) => setTemplate({...template, border_color: e.target.value})}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>Text Color</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="color"
                      value={template.text_color}
                      onChange={(e) => setTemplate({...template, text_color: e.target.value})}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={template.text_color}
                      onChange={(e) => setTemplate({...template, text_color: e.target.value})}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>{isThacharyCurve ? 'Swoosh / Accent' : 'Accent Color'}</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="color"
                      value={template.accent_color}
                      onChange={(e) => setTemplate({...template, accent_color: e.target.value})}
                      className="w-12 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={template.accent_color}
                      onChange={(e) => setTemplate({...template, accent_color: e.target.value})}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Text Content */}
          <Card>
            <CardHeader>
              <CardTitle>Text Content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{isThacharyCurve ? 'Header Title (line 1)' : 'Certificate Title'}</Label>
                <Input
                  value={template.title_text}
                  onChange={(e) => setTemplate({...template, title_text: e.target.value})}
                />
              </div>
              <div>
                <Label>{isThacharyCurve ? 'Header Title (line 2)' : 'Subtitle Text'}</Label>
                <Input
                  value={template.subtitle_text}
                  onChange={(e) => setTemplate({...template, subtitle_text: e.target.value})}
                />
              </div>
              {isThacharyCurve && (
                <div>
                  <Label>Presentation Line</Label>
                  <Input
                    value={template.presentation_text}
                    onChange={(e) => setTemplate({...template, presentation_text: e.target.value})}
                  />
                </div>
              )}
              <div>
                <Label>Completion Text</Label>
                <Input
                  value={template.completion_text}
                  onChange={(e) => setTemplate({...template, completion_text: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Organization Name</Label>
                  <Input
                    value={template.organization_name}
                    onChange={(e) => setTemplate({...template, organization_name: e.target.value})}
                  />
                </div>
                <div>
                  <Label>{isThacharyCurve ? 'Header Tagline' : 'Organization Tagline'}</Label>
                  <Input
                    value={template.organization_tagline}
                    onChange={(e) => setTemplate({...template, organization_tagline: e.target.value})}
                  />
                </div>
              </div>
              {isThacharyCurve && (
                <div>
                  <Label>Date Label</Label>
                  <Input
                    value={template.date_label}
                    onChange={(e) => setTemplate({...template, date_label: e.target.value})}
                  />
                </div>
              )}
              {isThacharyCurve && (
                <div>
                  <Label>Left Panel Text (optional)</Label>
                  <Textarea
                    value={template.left_panel_text}
                    onChange={(e) => setTemplate({...template, left_panel_text: e.target.value})}
                    rows={3}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Typography */}
          <Card>
            <CardHeader>
              <CardTitle>Typography</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Font Family</Label>
                <Select 
                  value={template.font_family} 
                  onValueChange={(v) => setTemplate({...template, font_family: v})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="serif">Serif (Classic)</SelectItem>
                    <SelectItem value="sans-serif">Sans-serif (Modern)</SelectItem>
                    <SelectItem value="cursive">Script (Elegant)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Title Size</Label>
                  <Input 
                    type="number" 
                    value={template.title_size} 
                    onChange={(e) => setTemplate({...template, title_size: parseInt(e.target.value)})}
                    min={24}
                    max={72}
                  />
                </div>
                <div>
                  <Label>Name Size</Label>
                  <Input 
                    type="number" 
                    value={template.name_size} 
                    onChange={(e) => setTemplate({...template, name_size: parseInt(e.target.value)})}
                    min={18}
                    max={48}
                  />
                </div>
                <div>
                  <Label>Course Size</Label>
                  <Input 
                    type="number" 
                    value={template.course_size} 
                    onChange={(e) => setTemplate({...template, course_size: parseInt(e.target.value)})}
                    min={14}
                    max={36}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Options */}
          <Card>
            <CardHeader>
              <CardTitle>Options</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Show Border</Label>
                <Switch
                  checked={template.show_border}
                  onCheckedChange={(v) => setTemplate({...template, show_border: v})}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Show Organization Seal</Label>
                <Switch
                  checked={template.show_seal}
                  onCheckedChange={(v) => setTemplate({...template, show_seal: v})}
                />
              </div>
              <div>
                <Label>Seal Image</Label>
                <div className="flex items-center gap-3 mt-1">
                  {template.seal_image_url && (
                    <img
                      src={`${BACKEND_URL}${template.seal_image_url}`}
                      alt="Seal"
                      className="w-12 h-12 object-contain rounded border"
                    />
                  )}
                  <label className="inline-flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer text-sm hover:bg-muted">
                    <Upload className="w-4 h-4" />
                    {uploadingField === 'seal_image_url' ? 'Uploading...' : 'Upload Seal'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleTemplateImageUpload(e, 'seal_image_url')}
                    />
                  </label>
                  {template.seal_image_url && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setTemplate({...template, seal_image_url: ''})}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              <div>
                <Label>Logo URL (optional)</Label>
                <Input
                  value={template.logo_url}
                  onChange={(e) => setTemplate({...template, logo_url: e.target.value})}
                  placeholder="https://..."
                />
              </div>
            </CardContent>
          </Card>

          {/* Photo Placement */}
          <Card>
            <CardHeader>
              <CardTitle>Student Photo</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Show Student Photo</Label>
                <Switch
                  checked={template.show_photo}
                  onCheckedChange={(v) => setTemplate({...template, show_photo: v})}
                />
              </div>
              <div>
                <Label>Photo Position</Label>
                <Select
                  value={template.photo_position}
                  onValueChange={(v) => setTemplate({...template, photo_position: v})}
                  disabled={!template.show_photo}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="top-left">Top Left</SelectItem>
                    <SelectItem value="top-center">Top Center</SelectItem>
                    <SelectItem value="top-right">Top Right</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Signatories */}
          <Card>
            <CardHeader>
              <CardTitle>Signatories</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {(template.signatories || []).map((sig, idx) => (
                <div key={idx} className="border rounded-md p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="font-medium">Signatory {idx + 1}</Label>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={sig.show !== false}
                        onCheckedChange={(v) => updateSignatory(idx, 'show', v)}
                      />
                      <Button variant="ghost" size="sm" onClick={() => removeSignatory(idx)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {sig.image_url && (
                      <img
                        src={`${BACKEND_URL}${sig.image_url}`}
                        alt={`Signature ${idx + 1}`}
                        className="h-10 object-contain rounded border bg-white"
                      />
                    )}
                    <label className="inline-flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer text-sm hover:bg-muted">
                      <Upload className="w-4 h-4" />
                      {uploadingField === `signatory_${idx}` ? 'Uploading...' : 'Upload Signature'}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleTemplateImageUpload(e, `signatory_${idx}`)}
                      />
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Name</Label>
                      <Input
                        value={sig.name || ''}
                        onChange={(e) => updateSignatory(idx, 'name', e.target.value)}
                        placeholder="Jane Smith"
                      />
                    </div>
                    <div>
                      <Label>Title</Label>
                      <Input
                        value={sig.title || ''}
                        onChange={(e) => updateSignatory(idx, 'title', e.target.value)}
                        placeholder="Director"
                      />
                    </div>
                  </div>
                </div>
              ))}
              <Button
                variant="outline"
                onClick={addSignatory}
                disabled={(template.signatories || []).length >= maxSignatories}
                className="w-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Signatory
              </Button>
            </CardContent>
          </Card>

          {/* Preview Data */}
          <Card>
            <CardHeader>
              <CardTitle>Preview Data</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Student Name</Label>
                <Input 
                  value={previewName} 
                  onChange={(e) => setPreviewName(e.target.value)}
                />
              </div>
              <div>
                <Label>Course Name</Label>
                <Input 
                  value={previewCourse} 
                  onChange={(e) => setPreviewCourse(e.target.value)}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Preview Panel */}
        <div className="sticky top-6">
          <Card>
            <CardHeader>
              <CardTitle>Live Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <CertificatePreview template={template} previewName={previewName} previewCourse={previewCourse} backendUrl={BACKEND_URL} />

              <p className="text-sm text-muted-foreground mt-4 text-center">
                This is a preview. Actual PDF certificates will be generated using these settings.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};



// ==================== ADMIN CATEGORIES ====================
export const AdminCategories = () => {
  const { token } = useAuth();
  const { t, tFormat } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    id: '', name: '', description: '', icon: '', order: 0, is_active: true
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/categories?include_inactive=true`, { headers });
      setCategories(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await axios.put(`${API_URL}/admin/categories/${editingCategory?.id}`, formData, { headers });
        toast.success(t('admin_toast_category_updated', 'Category updated'));
      } else {
        await axios.post(`${API_URL}/admin/categories`, formData, { headers });
        toast.success(t('admin_toast_category_created', 'Category created'));
      }
      setDialogOpen(false);
      setEditingCategory(null);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_toast_category_save_fail', 'Failed to save category'));
    }
  };

  const handleEdit = (category) => {
    if (!category) return;
    setEditingCategory(category);
    setFormData({
      id: category?.id || '',
      name: category?.name || '',
      description: category?.description || '',
      icon: category?.icon || '',
      order: category?.order || 0,
      is_active: category?.is_active !== false
    });
    setDialogOpen(true);
  };

  const handleDelete = async (categoryId) => {
    if (!categoryId || !window.confirm(t('admin_confirm_delete_category', 'Delete this category?'))) return;
    try {
      await axios.delete(`${API_URL}/admin/categories/${categoryId}`, { headers });
      toast.success(t('admin_toast_category_deleted', 'Category deleted'));
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_toast_category_delete_fail', 'Failed to delete category'));
    }
  };

  const handleToggleActive = async (category) => {
    if (!category?.id) return;
    try {
      await axios.put(`${API_URL}/admin/categories/${category.id}`, { is_active: !category?.is_active }, { headers });
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('admin_toast_category_update_fail', 'Failed to update category'));
    }
  };

  const resetForm = () => {
    setFormData({ id: '', name: '', description: '', icon: '', order: 0, is_active: true });
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div data-testid="admin-categories">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-serif font-medium">Categories</h1>
          <p className="text-muted-foreground">{categories.length} categories</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { setEditingCategory(null); resetForm(); }}>
              <Plus className="w-4 h-4 mr-2" />
              Create Category
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingCategory ? 'Edit Category' : 'Create Category'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Name *</Label>
                <Input 
                  value={formData.name} 
                  onChange={(e) => setFormData({...formData, name: e.target.value, id: editingCategory ? formData.id : e.target.value.toLowerCase().replace(/\s+/g, '_')})} 
                  required 
                />
              </div>
              <div>
                <Label>ID (slug)</Label>
                <Input 
                  value={formData.id} 
                  onChange={(e) => setFormData({...formData, id: e.target.value})} 
                  placeholder="auto-generated" 
                  disabled={!!editingCategory}
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea 
                  value={formData.description} 
                  onChange={(e) => setFormData({...formData, description: e.target.value})} 
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Icon (emoji or code)</Label>
                  <Input 
                    value={formData.icon} 
                    onChange={(e) => setFormData({...formData, icon: e.target.value})} 
                    placeholder="e.g. 🏠"
                  />
                </div>
                <div>
                  <Label>Order</Label>
                  <Input 
                    type="number" 
                    value={formData.order} 
                    onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})} 
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={formData.is_active} onCheckedChange={(v) => setFormData({...formData, is_active: v})} />
                <Label>Active</Label>
              </div>
              <DialogFooter>
                <Button type="submit">{editingCategory ? 'Update' : 'Create'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>ID</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Courses</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.map((cat) => (
                <TableRow key={cat.id}>
                  <TableCell className="font-mono">{cat.order}</TableCell>
                  <TableCell className="font-medium">
                    {cat.icon && <span className="mr-2">{cat.icon}</span>}
                    {cat.name}
                  </TableCell>
                  <TableCell className="font-mono text-muted-foreground">{cat.id}</TableCell>
                  <TableCell className="max-w-xs truncate">{cat.description}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">{cat.course_count || 0}</Badge>
                  </TableCell>
                  <TableCell>
                    <Switch checked={cat.is_active} onCheckedChange={() => handleToggleActive(cat)} />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(cat)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(cat.id)}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {categories.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No categories yet. Create your first category.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Share Rewards (WhatsApp Share & Earn)
export const AdminShareRewards = () => {
  const { token } = useAuth();
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const [configRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/share-rewards/config`, { headers }),
        axios.get(`${API_URL}/admin/share-rewards/stats`, { headers }),
      ]);
      setConfig(configRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/share-rewards/config`, config, { headers });
      toast.success('Share Rewards settings saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-32 bg-muted rounded-xl" />
        <div className="h-64 bg-muted rounded-xl" />
      </div>
    );
  }

  return (
    <div data-testid="admin-share-rewards">
      <div className="mb-8">
        <h1 className="text-3xl font-serif font-medium">Share Rewards</h1>
        <p className="text-muted-foreground">WhatsApp Share & Earn configuration and stats</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center">
                <MousePointerClick className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_verified_clicks || 0}</p>
                <p className="text-sm text-muted-foreground">Verified Clicks</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center">
                <Share2 className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_taps || 0}</p>
                <p className="text-sm text-muted-foreground">Share Button Taps</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center">
                <Gift className="w-6 h-6 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.coupons_issued || 0}</p>
                <p className="text-sm text-muted-foreground">Coupons Issued</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center">
                <Award className="w-6 h-6 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.coupons_redeemed || 0}</p>
                <p className="text-sm text-muted-foreground">Coupons Redeemed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Config */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 max-w-2xl">
          <div className="flex items-center justify-between">
            <div>
              <Label>Enable Share & Earn</Label>
              <p className="text-sm text-muted-foreground">Show the Share & Earn section on the user dashboard</p>
            </div>
            <Switch
              checked={config.enabled}
              onCheckedChange={(v) => setConfig({ ...config, enabled: v })}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Verified Shares Required</Label>
              <Input
                type="number"
                min={1}
                value={config.shares_required}
                onChange={(e) => setConfig({ ...config, shares_required: parseInt(e.target.value) || 1 })}
              />
            </div>
            <div>
              <Label>Reward Percentage (%)</Label>
              <Input
                type="number"
                min={1}
                max={100}
                value={config.reward_percent}
                onChange={(e) => setConfig({ ...config, reward_percent: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>Coupon Validity (days)</Label>
              <Input
                type="number"
                min={1}
                value={config.coupon_validity_days}
                onChange={(e) => setConfig({ ...config, coupon_validity_days: parseInt(e.target.value) || 1 })}
              />
            </div>
            <div>
              <Label>Max Rewards Per User / Month</Label>
              <Input
                type="number"
                min={1}
                value={config.max_rewards_per_user_month}
                onChange={(e) => setConfig({ ...config, max_rewards_per_user_month: parseInt(e.target.value) || 1 })}
              />
            </div>
          </div>

          <div>
            <Label>WhatsApp Message Template</Label>
            <Textarea
              value={config.message_template}
              onChange={(e) => setConfig({ ...config, message_template: e.target.value })}
              rows={3}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Use <code className="font-mono">{'{link}'}</code> as a placeholder for the user's share link.
            </p>
          </div>

          <Button onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </CardContent>
      </Card>

      {/* Top Sharers */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">Top Sharers</h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Verified Clicks</TableHead>
                <TableHead>Rewards Issued</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(stats?.top_sharers || []).map((s) => (
                <TableRow key={s.user_id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{s.name}</p>
                      <p className="text-sm text-muted-foreground">{s.email}</p>
                    </div>
                  </TableCell>
                  <TableCell>{s.verified_clicks}</TableCell>
                  <TableCell>{s.rewards_issued}</TableCell>
                </TableRow>
              ))}
              {(!stats?.top_sharers || stats.top_sharers.length === 0) && (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                    No shares yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
